import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../../hooks/useApi';
import { fmtDateTime } from '../../../utils/ruDates';

function AdminNewsAutomation() {
    const { request } = useApi();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [activeTab, setActiveTab] = useState('raw'); // 'raw', 'shortlisted', 'published', 'rejected'
    const [showSourceForm, setShowSourceForm] = useState(false);
    const [sourceFormData, setSourceFormData] = useState({ name: '', type: 'rss', url: '', keywords: '' });

    useEffect(() => {
        fetchItems();
        fetchSources();
    }, [activeTab]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const data = await request(`/api/v1/admin/news-automation/items?status=${activeTab}&page=1&per_page=50`);
            setItems(data.items || []);
        } catch (err) {
            console.error('Failed to fetch news items:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSources = async () => {
        try {
            const data = await request('/api/v1/admin/news-automation/sources');
            setSources(data.sources || []);
        } catch (err) {
            console.error('Failed to fetch sources:', err);
        }
    };

    const handleFetchNews = async () => {
        setFetching(true);
        try {
            const result = await request('/api/v1/admin/news-automation/fetch', {
                method: 'POST',
            });
            await fetchItems();
            const errors = result.errors || [];
            if (errors.length > 0) {
                alert(
                    `Собрано ${result.rss_collected} новостей из ${result.sources_processed} источников.\n`
                    + `Ошибки:\n${errors.join('\n')}`
                );
            } else {
                alert(`Собрано ${result.rss_collected} новостей из ${result.sources_processed} источников`);
            }
        } catch (err) {
            alert('Ошибка при сборке: ' + err.message);
        } finally {
            setFetching(false);
        }
    };

    const handleShortlist = async (itemId) => {
        try {
            await request(`/api/v1/admin/news-automation/items/${itemId}/shortlist`, {
                method: 'POST',
            });
            await fetchItems();
        } catch (err) {
            alert('Ошибка: ' + err.message);
        }
    };

    const handleReject = async (itemId) => {
        try {
            await request(`/api/v1/admin/news-automation/items/${itemId}/reject`, {
                method: 'POST',
            });
            await fetchItems();
        } catch (err) {
            alert('Ошибка: ' + err.message);
        }
    };

    const handleGenerateArticle = async (itemId) => {
        try {
            const result = await request(`/api/v1/admin/news-automation/items/${itemId}/generate-article`, {
                method: 'POST',
            });
            // Navigate to blog editor with the generated article
            navigate(`/admin/blog/${result.article_id}/edit`);
        } catch (err) {
            alert('Ошибка при генерации: ' + err.message);
        }
    };

    const handleCreateSource = async () => {
        if (!sourceFormData.name || !sourceFormData.url) {
            alert('Заполните название и URL');
            return;
        }
        try {
            const keywords = sourceFormData.keywords
                .split(',')
                .map(k => k.trim())
                .filter(k => k.length > 0);
            await request('/api/v1/admin/news-automation/sources', {
                method: 'POST',
                body: JSON.stringify({
                    name: sourceFormData.name,
                    type: sourceFormData.type,
                    url: sourceFormData.url,
                    keywords,
                }),
            });
            setSourceFormData({ name: '', type: 'rss', url: '', keywords: '' });
            setShowSourceForm(false);
            await fetchSources();
        } catch (err) {
            alert('Ошибка: ' + err.message);
        }
    };

    const handleDeleteSource = async (sourceId) => {
        if (!window.confirm('Удалить источник?')) return;
        try {
            await request(`/api/v1/admin/news-automation/sources/${sourceId}`, {
                method: 'DELETE',
            });
            await fetchSources();
        } catch (err) {
            alert('Ошибка: ' + err.message);
        }
    };

    const handleToggleSourceActive = async (sourceId, isActive) => {
        try {
            await request(`/api/v1/admin/news-automation/sources/${sourceId}`, {
                method: 'PATCH',
                body: JSON.stringify({ is_active: !isActive }),
            });
            await fetchSources();
        } catch (err) {
            alert('Ошибка: ' + err.message);
        }
    };

    const statusLabels = {
        raw: 'Сырая фактура',
        shortlisted: 'В шортлисте',
        published: 'Опубликовано',
        rejected: 'Отклонено',
    };

    const statusColors = {
        raw: '#2196F3',
        shortlisted: '#FF9800',
        published: '#4CAF50',
        rejected: '#f44336',
    };

    return (
        <div className="admin-page">
            <div className="admin-page__header">
                <h1>Автоматизация новостей маркетплейсов</h1>
                <p>Сбор и управление новостями Ozon, Wildberries и других маркетплейсов</p>
            </div>

            <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                <button
                    className="btn btn-primary"
                    onClick={handleFetchNews}
                    disabled={fetching}
                >
                    {fetching ? '⏳ Собираю новости...' : '🔄 Собрать новости'}
                </button>
            </div>

            {/* Вкладки */}
            <div style={{ borderBottom: '1px solid #ddd', marginBottom: '20px', display: 'flex', gap: '0' }}>
                {Object.entries(statusLabels).map(([status, label]) => (
                    <button
                        key={status}
                        onClick={() => setActiveTab(status)}
                        style={{
                            padding: '12px 20px',
                            border: 'none',
                            backgroundColor: activeTab === status ? '#f5f5f5' : 'white',
                            borderBottom: activeTab === status ? `3px solid ${statusColors[status]}` : 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: activeTab === status ? 'bold' : 'normal',
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Список новостей */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    Загрузка...
                </div>
            ) : items.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    Новостей не найдено
                </div>
            ) : (
                <div style={{ display: 'grid', gap: '15px' }}>
                    {items.map((item) => (
                        <div
                            key={item.id}
                            style={{
                                padding: '15px',
                                border: `2px solid ${statusColors[item.status]}`,
                                borderRadius: '6px',
                                backgroundColor: '#fafafa',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ margin: '0 0 5px 0' }}>{item.headline}</h4>
                                    <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
                                        <strong>Источник:</strong> {item.source_name}
                                    </p>
                                    {item.marketplaces && item.marketplaces.length > 0 && (
                                        <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
                                            <strong>Площадки:</strong> {item.marketplaces.join(', ')}
                                        </p>
                                    )}
                                    {item.topic && (
                                        <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
                                            <strong>Тема:</strong> {item.topic}
                                        </p>
                                    )}
                                    <p style={{ margin: '5px 0', fontSize: '12px', color: '#999' }}>
                                        {fmtDateTime(item.published_at)}
                                    </p>
                                </div>
                                <div style={{
                                    display: 'inline-block',
                                    padding: '4px 12px',
                                    backgroundColor: statusColors[item.status],
                                    color: 'white',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    whiteSpace: 'nowrap',
                                }}>
                                    Важность: {item.importance_score || 0}
                                </div>
                            </div>

                            {item.content_fragment && (
                                <p style={{ margin: '10px 0', fontSize: '13px', color: '#555', lineHeight: '1.4' }}>
                                    {item.content_fragment}
                                </p>
                            )}

                            {/* Действия */}
                            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #ddd' }}>
                                {activeTab === 'raw' && (
                                    <>
                                        <button
                                            className="btn btn-small"
                                            onClick={() => handleShortlist(item.id)}
                                            style={{ backgroundColor: '#FF9800', color: 'white' }}
                                        >
                                            В шортлист
                                        </button>
                                        <button
                                            className="btn btn-small"
                                            onClick={() => handleReject(item.id)}
                                            style={{ backgroundColor: '#f44336', color: 'white' }}
                                        >
                                            Отклонить
                                        </button>
                                    </>
                                )}
                                {activeTab === 'shortlisted' && (
                                    <>
                                        <button
                                            className="btn btn-small"
                                            onClick={() => handleGenerateArticle(item.id)}
                                            style={{ backgroundColor: '#4CAF50', color: 'white' }}
                                        >
                                            Генерировать статью
                                        </button>
                                        <button
                                            className="btn btn-small"
                                            onClick={() => handleReject(item.id)}
                                            style={{ backgroundColor: '#f44336', color: 'white' }}
                                        >
                                            Отклонить
                                        </button>
                                    </>
                                )}
                                <a
                                    href={item.source_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-small"
                                    style={{ textDecoration: 'none' }}
                                >
                                    Источник ↗
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* РАЗДЕЛ ИСТОЧНИКИ */}
            <div style={{ marginTop: '40px', paddingTop: '30px', borderTop: '2px solid #e0e0e0' }}>
                <h2>Источники новостей (RSS)</h2>

                {/* Кнопка добавления источника */}
                {!showSourceForm && (
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            setSourceFormData({ ...sourceFormData, type: 'rss' });
                            setShowSourceForm(true);
                        }}
                        style={{ marginBottom: '20px' }}
                    >
                        + Добавить источник
                    </button>
                )}

                {/* Форма добавления */}
                {showSourceForm && (
                    <div style={{
                        padding: '15px',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '6px',
                        marginBottom: '20px',
                        border: '1px solid #ddd',
                    }}>
                        <h4>Новый источник</h4>
                        <div style={{ marginBottom: '10px' }}>
                            <label>Название:</label>
                            <input
                                type="text"
                                value={sourceFormData.name}
                                onChange={(e) => setSourceFormData({ ...sourceFormData, name: e.target.value })}
                                style={{ width: '100%', padding: '8px', marginTop: '5px', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <label>URL:</label>
                            <input
                                type="text"
                                value={sourceFormData.url}
                                onChange={(e) => setSourceFormData({ ...sourceFormData, url: e.target.value })}
                                placeholder="https://example.com/rss"
                                style={{ width: '100%', padding: '8px', marginTop: '5px', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <label>Ключевые слова (через запятую):</label>
                            <input
                                type="text"
                                value={sourceFormData.keywords}
                                onChange={(e) => setSourceFormData({ ...sourceFormData, keywords: e.target.value })}
                                placeholder="ozon, wildberries, комиссии"
                                style={{ width: '100%', padding: '8px', marginTop: '5px', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                className="btn btn-primary"
                                onClick={handleCreateSource}
                            >
                                Создать
                            </button>
                            <button
                                className="btn"
                                onClick={() => setShowSourceForm(false)}
                                style={{ backgroundColor: '#ccc', color: '#333' }}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                )}

                {/* Список источников */}
                <div style={{ display: 'grid', gap: '15px' }}>
                    {sources
                        .filter(s => s.type === 'rss')
                        .map((source) => (
                            <div
                                key={source.id}
                                style={{
                                    padding: '15px',
                                    border: `2px solid ${source.is_active ? '#4CAF50' : '#ccc'}`,
                                    borderRadius: '6px',
                                    backgroundColor: source.is_active ? '#f0f8f0' : '#fafafa',
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                    <div style={{ flex: 1 }}>
                                        <h4 style={{ margin: '0 0 8px 0' }}>{source.name}</h4>
                                        <p style={{ margin: '5px 0', fontSize: '12px', color: '#666', wordBreak: 'break-all' }}>
                                            <strong>URL:</strong> {source.url}
                                        </p>
                                        {source.keywords && source.keywords.length > 0 && (
                                            <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
                                                <strong>Ключевые слова:</strong>{' '}
                                                {source.keywords.map((kw, i) => (
                                                    <span
                                                        key={i}
                                                        style={{
                                                            display: 'inline-block',
                                                            backgroundColor: '#e3f2fd',
                                                            padding: '2px 8px',
                                                            borderRadius: '12px',
                                                            marginRight: '5px',
                                                            marginTop: '5px',
                                                            fontSize: '12px',
                                                        }}
                                                    >
                                                        {kw}
                                                    </span>
                                                ))}
                                            </p>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                                        <button
                                            className="btn btn-small"
                                            onClick={() => handleToggleSourceActive(source.id, source.is_active)}
                                            style={{
                                                backgroundColor: source.is_active ? '#FF9800' : '#4CAF50',
                                                color: 'white',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {source.is_active ? 'Выкл' : 'Вкл'}
                                        </button>
                                        <button
                                            className="btn btn-small"
                                            onClick={() => handleDeleteSource(source.id)}
                                            style={{ backgroundColor: '#f44336', color: 'white' }}
                                        >
                                            Удалить
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    {sources.filter(s => s.type === 'rss').length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                            Нет RSS лент
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminNewsAutomation;
